import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import SpinnerOverlay from '../../components/ui/SpinnerOverlay';
import SuccessToast from '../../components/ui/SuccessToast';

// Signed URL thumbnail for vehicle media
const VehicleThumbnail = ({ media }) => {
  const [imgUrl, setImgUrl] = useState(null);
  const primaryMedia = media?.find(m => m?.is_primary) || media?.[0];

  useEffect(() => {
    if (primaryMedia?.storage_path) {
      supabase?.storage
        ?.from('vehicle-media')
        ?.createSignedUrl(primaryMedia?.storage_path, 3600)
        ?.then(({ data }) => { if (data?.signedUrl) setImgUrl(data?.signedUrl); });
    }
  }, [primaryMedia?.storage_path]);

  if (imgUrl) {
    return (
      <div className="w-20 h-16 rounded-xl overflow-hidden flex-shrink-0">
        <img src={imgUrl} alt="Vehicle" className="w-full h-full object-cover" />
      </div>
    );
  }
  return (
    <div className="w-20 h-16 rounded-xl bg-white/10 flex items-center justify-center text-3xl flex-shrink-0">🚗</div>
  );
};

const VehicleReviewQueueScreen = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('pending_review');

  useEffect(() => {
    fetchVehicles();
  }, [filterStatus]);

  const fetchVehicles = async () => {
    setLoading(true);
    try {
      let query = supabase?.from('vehicles')
        ?.select(`*, vehicle_pricing(*), vehicle_media(*), user_profiles!vehicles_owner_id_fkey(full_name, email, phone)`)
        ?.order('created_at', { ascending: false });
      if (filterStatus !== 'all') query = query?.eq('status', filterStatus);
      const { data, error } = await query;
      if (!error) setVehicles(data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleApprove = async (vehicleId) => {
    setProcessing(vehicleId);
    try {
      const { error } = await supabase
        ?.from('vehicles')
        ?.update({ status: 'active' })
        ?.eq('id', vehicleId);
      if (error) throw error;
      setSuccessMsg('Vehicle listing approved!');
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2000);
      fetchVehicles();
    } catch (e) { console.error(e); }
    finally { setProcessing(null); }
  };

  const handleReject = async () => {
    if (!selectedVehicle) return;
    setProcessing(selectedVehicle?.id);
    try {
      const { error } = await supabase
        ?.from('vehicles')
        ?.update({ status: 'suspended' })
        ?.eq('id', selectedVehicle?.id);
      if (error) throw error;
      setSuccessMsg('Vehicle listing rejected');
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2000);
      setShowRejectModal(false);
      setRejectReason('');
      setSelectedVehicle(null);
      fetchVehicles();
    } catch (e) { console.error(e); }
    finally { setProcessing(null); }
  };

  const statusConfig = {
    pending_review: { label: 'Pending Review', color: '#fbbf24', bg: 'rgba(251,191,36,0.12)' },
    active: { label: 'Active', color: '#34d399', bg: 'rgba(52,211,153,0.12)' },
    suspended: { label: 'Suspended', color: '#f87171', bg: 'rgba(248,113,113,0.12)' },
    draft: { label: 'Draft', color: '#94a3b8', bg: 'rgba(148,163,184,0.12)' },
  };

  const formatDate = (d) => d ? new Date(d)?.toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

  return (
    <div className="min-h-screen" style={{ background: '#0a1628', fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900&display=swap');`}</style>
      {processing && <SpinnerOverlay message="Processing..." />}
      {/* Add visible prop to SuccessToast */}
      {showSuccess && <SuccessToast message={successMsg} visible={showSuccess} subMessage="" onDismiss={() => setShowSuccess(false)} />}
      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)' }}>
          <div className="rounded-2xl p-6 w-full max-w-md" style={{ background: '#0d1f3c', border: '1px solid rgba(255,255,255,0.1)' }}>
            <h3 className="text-white font-bold text-lg mb-3">Reject Listing</h3>
            <p className="text-white/50 text-sm mb-4">Provide a reason for rejection (will be shared with owner)</p>
            <textarea rows={4} placeholder="e.g. Insufficient photos, missing insurance document..." value={rejectReason} onChange={e => setRejectReason(e?.target?.value)}
              className="w-full px-4 py-3 rounded-xl text-white placeholder-white/30 text-sm outline-none resize-none mb-4"
              style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)' }} />
            <div className="flex gap-3">
              <button onClick={() => { setShowRejectModal(false); setSelectedVehicle(null); }} className="btn-secondary flex-1">Cancel</button>
              <button onClick={handleReject} className="btn-danger flex-1">Reject Listing</button>
            </div>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="sticky top-0 z-50 border-b border-white/10" style={{ background: 'rgba(10,22,40,0.95)', backdropFilter: 'blur(20px)' }}>
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/admin-dashboard')} className="p-2 rounded-xl hover:bg-white/10 transition-colors">
              <Icon name="ArrowLeft" size={20} className="text-white" />
            </button>
            <h1 className="text-white font-bold text-lg">Vehicle Review Queue</h1>
          </div>
          <span className="px-3 py-1 rounded-full text-xs font-bold" style={{ background: 'rgba(251,191,36,0.15)', color: '#fbbf24' }}>
            {vehicles?.filter(v => v?.status === 'pending_review')?.length} pending
          </span>
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Filter */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { key: 'pending_review', label: 'Pending Review' },
            { key: 'active', label: 'Active' },
            { key: 'suspended', label: 'Suspended' },
            { key: 'all', label: 'All' },
          ]?.map(f => (
            <button key={f?.key} onClick={() => setFilterStatus(f?.key)}
              className="flex-shrink-0 px-4 py-2 rounded-xl text-sm font-semibold transition-all"
              style={{ background: filterStatus === f?.key ? 'rgba(255,215,0,0.15)' : 'rgba(255,255,255,0.06)', color: filterStatus === f?.key ? '#ffd700' : 'rgba(255,255,255,0.5)', border: `1px solid ${filterStatus === f?.key ? 'rgba(255,215,0,0.3)' : 'rgba(255,255,255,0.1)'}` }}>
              {f?.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3]?.map(i => <div key={i} className="h-40 rounded-2xl animate-pulse" style={{ background: 'rgba(255,255,255,0.05)' }} />)}
          </div>
        ) : vehicles?.length === 0 ? (
          <div className="text-center py-20">
            <div className="text-5xl mb-3">✅</div>
            <h3 className="text-white font-bold text-lg mb-1">Queue is clear</h3>
            <p className="text-white/40 text-sm">No vehicles pending review</p>
          </div>
        ) : (
          <div className="space-y-4">
            {vehicles?.map(vehicle => {
              const status = statusConfig?.[vehicle?.status] || statusConfig?.draft;
              const pricing = vehicle?.vehicle_pricing?.[0];
              const owner = vehicle?.user_profiles;
              const mediaCount = vehicle?.vehicle_media?.length || 0;
              const completeness = Math.min(100, Math.round(
                ((vehicle?.title ? 15 : 0) + (vehicle?.make ? 10 : 0) + (vehicle?.model ? 10 : 0) +
                  (vehicle?.location_address ? 15 : 0) + (pricing ? 20 : 0) + (mediaCount >= 6 ? 30 : mediaCount * 5))
              ));

              return (
                <div key={vehicle?.id} className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <div className="flex items-start gap-4">
                    <VehicleThumbnail media={vehicle?.vehicle_media || []} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h3 className="text-white font-bold text-base">{vehicle?.title}</h3>
                        <span className="flex-shrink-0 px-2 py-0.5 rounded-full text-xs font-bold" style={{ background: status?.bg, color: status?.color }}>{status?.label}</span>
                      </div>
                      <p className="text-white/40 text-xs mb-2">{vehicle?.make} {vehicle?.model} {vehicle?.year} · {vehicle?.location_address}</p>
                      <div className="flex flex-wrap gap-4 text-xs text-white/50">
                        <span>Owner: <span className="text-white">{owner?.full_name || 'N/A'}</span></span>
                        <span>Photos: <span className={mediaCount >= 6 ? 'text-[#34d399]' : 'text-red-400'}>{mediaCount}</span></span>
                        <span>Daily Rate: <span className="text-[#ffd700]">KES {pricing?.base_daily_rate?.toLocaleString() || 'Not set'}</span></span>
                        <span>Submitted: <span className="text-white">{formatDate(vehicle?.created_at)}</span></span>
                      </div>
                    </div>
                  </div>

                  {/* Completeness Bar */}
                  <div className="mt-4 mb-4">
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-white/40">Listing Completeness</span>
                      <span className={completeness >= 80 ? 'text-[#34d399]' : completeness >= 50 ? 'text-[#fbbf24]' : 'text-red-400'}>{completeness}%</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-white/10">
                      <div className="h-full rounded-full transition-all" style={{ width: `${completeness}%`, background: completeness >= 80 ? '#34d399' : completeness >= 50 ? '#fbbf24' : '#f87171' }} />
                    </div>
                  </div>

                  {vehicle?.status === 'pending_review' && (
                    <div className="flex gap-3">
                      <button onClick={() => handleApprove(vehicle?.id)} disabled={!!processing} className="btn-primary flex-1 text-sm py-2.5">
                        <Icon name="CheckCircle" size={16} /> Approve Listing
                      </button>
                      <button onClick={() => { setSelectedVehicle(vehicle); setShowRejectModal(true); }} disabled={!!processing} className="btn-danger flex-1 text-sm py-2.5">
                        <Icon name="XCircle" size={16} /> Reject
                      </button>
                    </div>
                  )}
                  {vehicle?.status === 'active' && (
                    <button onClick={() => { setSelectedVehicle(vehicle); setShowRejectModal(true); }} className="btn-danger text-sm py-2 px-4">
                      <Icon name="Ban" size={14} /> Suspend Listing
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default VehicleReviewQueueScreen;
